import { Button, Descriptions, Empty, Modal, Typography } from 'antd'
import { option } from 'fp-ts'
import { flow, pipe } from 'fp-ts/lib/function'
import * as React from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import DiscountRow from '../Discounts/DiscountRow'
import { ShowPayer } from '../Payers/ShowPayer'
import BillLayout from './BillLayout'
import { ShowInvoiceItems } from './ShowInvoiceItems'
import TotalBlock from './TotalBlock'
import { closeViewAction, emptyBill } from './types'
import { useMode, usePublishedInvoices, useUnpublishInvoice } from './queries'
import { FinancingStatus } from './FinancingStatus'
import styled from 'styled-components'
import { default as logo } from '../../assets/unpublishInvoice.svg'
import ShowCompanyInfo from './ShowCompanyInfo'
import { useOrganization } from '@library/react-toolkit'
import OrganizationLogo from './BillForm/OrganizationLogo'

const InvoiceTitle = styled(Typography.Title)`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
`

const AddBillModal = () => {
  const visible = useSelector(s => option.isSome(s.bills.viewBill))
  const bill = useSelector(s =>
    pipe(
      s.bills.viewBill,
      option.getOrElse(() => emptyBill)
    )
  )

  const mode = useMode()
  const dispatch = useDispatch()
  const getPublishedBills = usePublishedInvoices()
  const unpublishInvoice = useUnpublishInvoice()
  const org = useOrganization()

  return (
    <Modal
      zIndex={1}
      visible={visible}
      onOk={flow(closeViewAction, dispatch)}
      onCancel={flow(closeViewAction, dispatch)}
      destroyOnClose={true}
      width="100%"
      style={{ maxWidth: '800px' }}
      confirmLoading={getPublishedBills.isLoading}
    >
      {getPublishedBills.data?.find(pb => pb.id === bill.id)?.status ===
      'Published' ? (
        <Button
          style={{
            float: 'right',
            marginRight: 15,
          }}
          onClick={() => {
            unpublishInvoice.mutate(bill.id, {
              onSuccess: () => {
                dispatch(closeViewAction())
              },
            })
          }}
          icon={<img src={logo} style={{ verticalAlign: 'middle' }} />}
        >
          <FormattedMessage id="unpublishInvoice" />
        </Button>
      ) : null}

      <BillLayout
        title={
          <InvoiceTitle level={2}>
            <FormattedMessage id="invoice" />
            <FinancingStatus
              status={
                getPublishedBills.data?.find(pb => pb.id === bill.id)?.status ??
                ''
              }
            />
          </InvoiceTitle>
        }
        company={<ShowCompanyInfo name={bill.organization} />}
        image={
          <OrganizationLogo
            orgName={
              mode === 'organization' ? org?.name ?? '' : bill.organization
            }
          />
        }
        invoiceInfo={
          <Descriptions column={1}>
            <Descriptions.Item label={<FormattedMessage id="invoiceRef" />}>
              {bill.reference}
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage id="invoiceDate" />}>
              <FormattedDate
                value={bill.createdAt}
                year="numeric"
                month="long"
                day="numeric"
              />
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage id="dueDate" />}>
              <FormattedDate
                value={bill.dueAt}
                year="numeric"
                month="long"
                day="numeric"
              />
            </Descriptions.Item>
            {bill.payer && (
              <Descriptions.Item label={<FormattedMessage id="paidBy" />}>
                {bill.payer}
              </Descriptions.Item>
            )}
          </Descriptions>
        }
        payer={
          // TODO: show supplier or customer
          !!bill.customer && mode === 'organization' ? (
            <ShowPayer payer={bill.customer} />
          ) : (
            <ShowCompanyInfo name={bill.supplier} />
          )
        }
        description={<Typography.Paragraph></Typography.Paragraph>}
        items={<ShowInvoiceItems items={bill.items} unitID={bill.unitID} />}
        discounts={
          !!bill.discount ? (
            <DiscountRow
              {...bill.discount}
              total={bill.amount}
              unit={bill.unitID}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<FormattedMessage id="noDiscount" />}
            />
          )
        }
        total={
          <TotalBlock
            discount={bill.discount}
            tax={bill.tax}
            total={bill.amount}
            unit={bill.unitID}
          />
        }
      />
    </Modal>
  )
}

export default AddBillModal
